/**
 * The Markdown subset Viktor's answers are rendered with.
 *
 * Deliberately not a markdown library. A chat bubble is a few lines wide, so
 * much of the language has nowhere to render - images, block quotes and nested
 * lists all either overflow or collapse into something unreadable - and
 * shipping a parser for the whole grammar to every user to support a handful
 * of constructs is not a trade worth making. What Viktor actually writes is
 * bold, lists, `code`, code blocks, tables and links; anything outside that is
 * left as the literal characters he typed, which is the honest failure.
 *
 * Nothing here is ever interpreted as HTML: the text is cut into spans and
 * blocks and rendered as React nodes, so a `<script>` in an answer is six
 * characters in a paragraph. That is the whole of the sanitising.
 *
 * Applied ONLY to Viktor's rows. What a person types stays literal: an admin
 * writing "2 * 3 * 4" means multiplication, and one pasting `order_id_2` is
 * naming a field, not asking for italics.
 */

export type MarkdownSpan =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string };

export type MarkdownBlock =
  | { kind: 'paragraph'; spans: MarkdownSpan[] }
  | { kind: 'list'; ordered: boolean; items: MarkdownSpan[][] }
  /** A fenced block, verbatim - no inline markup is read inside it. */
  | { kind: 'code'; text: string }
  | { kind: 'table'; header: MarkdownSpan[][]; rows: MarkdownSpan[][][] };

/**
 * Inline constructs, in precedence order within one alternation so the first
 * one to start at a position wins.
 *
 * `code` is first because its content is literal - asterisks inside backticks
 * are characters, not emphasis. Underscore emphasis (`_italic_`, `__bold__`)
 * is deliberately NOT recognised: this product's text is full of identifiers
 * like `order_id` and `payment_report_income`, and every one of them would
 * turn half a sentence italic. Models reach for the asterisk forms anyway.
 *
 * Both emphasis forms require a non-space character against each marker, which
 * is what keeps a bullet ("* item") and arithmetic ("3 * 4 * 5") out of it.
 */
const INLINE_PATTERN =
  /`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*(\S(?:[^*]*\S)?)\*\*|\*(\S(?:[^*\n]*\S)?)\*/g;

/** One line of text, cut into its emphasis runs. */
const parseInline = (line: string): MarkdownSpan[] => {
  const spans: MarkdownSpan[] = [];
  let cursor = 0;

  for (const match of line.matchAll(INLINE_PATTERN)) {
    const start = match.index as number;
    if (start > cursor) {
      spans.push({ kind: 'text', text: line.slice(cursor, start) });
    }

    const [whole, code, linkText, href, bold, italic] = match;
    if (code !== undefined) spans.push({ kind: 'code', text: code });
    else if (linkText !== undefined && href !== undefined) {
      spans.push({ kind: 'link', text: linkText, href });
    } else if (bold !== undefined) spans.push({ kind: 'bold', text: bold });
    else if (italic !== undefined) {
      spans.push({ kind: 'italic', text: italic });
    }

    cursor = start + whole.length;
  }

  if (cursor < line.length) {
    spans.push({ kind: 'text', text: line.slice(cursor) });
  }
  return spans;
};

/**
 * Balance a marker that opens and never closes.
 *
 * Every frame of a streamed answer is a PREFIX of it, so the text routinely
 * ends in the middle of `**486 active locations**` - and a half-written marker
 * is not a marker, so the raw asterisks show through until the closer arrives.
 *
 * Closing it rather than stripping it keeps the phrase bold from its first
 * character, so nothing restyles under the reader as it types.
 *
 * The guard is what makes this safe on finished text: a marker is only closed
 * when what follows it would actually form a span. An odd asterisk pair in
 * "use ** for bold" is somebody talking about asterisks, and it stays one.
 */
const closeMarker = (text: string, marker: string) => {
  const parts = text.split(marker);
  // An odd number of pieces means an even number of markers - all matched.
  if (parts.length % 2 === 1) return text;

  const tail = parts[parts.length - 1];
  /* The opener arrived a frame ahead of the word it belongs to. There is
     nothing to style yet, so it is dropped until there is. */
  if (!tail) return parts.slice(0, -1).join(marker);
  return /^\S(?:[\s\S]*\S)?$/.test(tail) ? text + marker : text;
};

/**
 * The text with any marker left hanging by a partial answer closed off - see
 * `closeMarker`. Code spans first, since their content is literal and an
 * asterisk inside one is not emphasis.
 */
const balanceMarkup = (text: string) => {
  const closed = closeMarker(closeMarker(text, '`'), '**');
  /* A lone trailing asterisk is the first half of either marker, arriving one
     character ahead of the rest of it. Nothing can be inferred from it yet, so
     it is dropped for the frame or two before the rest lands. */
  return closed.replace(/(^|[^*])\*$/, '$1');
};

/* `\s{0,3}` on each: more indentation than that is a nested list, which this
   subset does not render - it stays a plain line rather than being silently
   flattened into the list above it, where it would claim to be a sibling of
   something it is a child of. */
const BULLET = /^\s{0,3}[-*•]\s+(.*)$/;
const ORDERED = /^\s{0,3}\d{1,2}[.)]\s+(.*)$/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;
/** A fence opens and closes a verbatim block; the info string is dropped. */
const FENCE = /^\s{0,3}```/;
/** A row: cells between pipes. The outer pipes are optional in GFM. */
const TABLE_ROW = /^\s*\|?.*\|.*$/;
/** The line under a header row that makes the two a table: `|---|:--:|`. */
const TABLE_RULE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

/**
 * A row's cells. A pipe inside a code span is a character, not a divider -
 * "the `a | b` field" is one cell - so the split skips those.
 */
const tableCells = (line: string) => {
  const cells: string[] = [];
  let cell = '';
  let inCode = false;
  for (const char of line.trim()) {
    if (char === '`') inCode = !inCode;
    if (char === '|' && !inCode) {
      cells.push(cell);
      cell = '';
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  /* The optional outer pipes leave an empty cell at each end. */
  if (cells.length && !cells[0].trim()) cells.shift();
  if (cells.length && !cells[cells.length - 1].trim()) cells.pop();
  return cells.map((text) => parseInline(text.trim()));
};

/**
 * The answer as one plain line, for an inbox row's preview.
 *
 * The row shows the raw body the server keeps, and Viktor's begins with
 * markup as often as not - `**486 active locations**`, `### Top 3` - which in a
 * one-line preview is punctuation with no meaning. Markers go, the words stay,
 * and the lines are joined on a space since the preview has no room for
 * structure anyway. Best effort by design: the row is a glance, not the answer.
 */
export const markdownToPreview = (text: string) =>
  text
    .replace(/```[^\n]*\n?/g, '')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}[-*•]\s+/gm, '')
    .replace(/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/gm, '')
    .replace(/\[([^\]\n]+)\]\([^)\s]+\)/g, '$1')
    .replace(/\*\*(\S(?:[^*]*\S)?)\*\*/g, '$1')
    .replace(/\*(\S(?:[^*\n]*\S)?)\*/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1')
    .replace(/\s*\|\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The body, cut into the blocks it is made of.
 *
 * A heading becomes a bold paragraph rather than a block of its own: a bubble
 * has no type scale to render one against, and this way a `### Locations`
 * still reads as the label it was meant to be instead of printing its hashes.
 *
 * Consecutive plain lines stay in ONE paragraph with their newlines intact,
 * because in a chat a line break is usually deliberate - markdown's rule that
 * two lines make one flowing sentence is written for documents.
 *
 * Markers left hanging are balanced first, so a half-arrived answer reads the
 * same as a finished one - see `balanceMarkup`. A fence or a table that has
 * not closed yet renders as far as it has arrived, which is the same rule.
 */
export const parseMarkdown = (text: string): MarkdownBlock[] => {
  const body = balanceMarkup(text);
  const lines = body.split('\n');
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: MarkdownSpan[][] } | null = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ kind: 'paragraph', spans: parseInline(paragraph.join('\n')) });
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    blocks.push({ kind: 'list', ordered: list.ordered, items: list.items });
    list = null;
  };

  const openItem = (ordered: boolean, text: string) => {
    flushParagraph();
    /* A list whose marker changed is a new list, not a continuation - one run
       of bullets followed by a run of steps is two different things. */
    if (list && list.ordered !== ordered) flushList();
    if (!list) list = { ordered, items: [] };
    list.items.push(parseInline(text));
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (FENCE.test(line)) {
      flushParagraph();
      flushList();
      const code: string[] = [];
      index += 1;
      /* Runs to the closing fence, or to the end of what has arrived. */
      while (index < lines.length && !FENCE.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }
      blocks.push({ kind: 'code', text: code.join('\n') });
      continue;
    }

    /* A table is a header row with a rule under it; anything that merely
       contains a pipe is text. The rule is what turns the row above into a
       header, so it is looked for one line ahead. */
    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[index + 1] ?? '')) {
      flushParagraph();
      flushList();
      const header = tableCells(line);
      const rows: MarkdownSpan[][][] = [];
      index += 2;
      while (index < lines.length && TABLE_ROW.test(lines[index])) {
        rows.push(tableCells(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    const bullet = line.match(BULLET);
    if (bullet) {
      openItem(false, bullet[1]);
      continue;
    }

    const ordered = line.match(ORDERED);
    if (ordered) {
      openItem(true, ordered[1]);
      continue;
    }

    flushList();

    if (!line.trim()) {
      flushParagraph();
      continue;
    }

    const heading = line.match(HEADING);
    paragraph.push(heading ? `**${heading[1]}**` : line);
  }

  flushList();
  flushParagraph();
  return blocks;
};
